import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getReaderCast, type ReaderCharacter } from "../api";
import CharacterFace from "../components/CharacterFace";
import Placeholder, { Pending } from "../components/Placeholder";
import { useT } from "../i18n";

/**
 * ReaderCharacters is the cast on the reader's contents page, spoiler-free
 * (requirement 0011): only the characters the reader has met, each as the
 * story introduced them. The server decides who has been met, from where the
 * reader has listened to. Nothing here spends.
 *
 * Folded until asked for: a reader opening a book has not come for a list of
 * people, so the page shows one line saying how many they have met. The
 * whole cast with what happens to everyone is **character arcs**, a row of
 * its own that says it holds spoilers before it is opened.
 */
export default function ReaderCharacters({ bookID }: { bookID: string }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const [arcs, setArcs] = useState(false);
  const met = useQuery({
    queryKey: ["characters", bookID, "reader", false],
    queryFn: () => getReaderCast(bookID, false),
    retry: false,
  });
  const arcCast = useQuery({
    queryKey: ["characters", bookID, "reader", true],
    queryFn: () => getReaderCast(bookID, true),
    enabled: arcs,
    retry: false,
  });
  const data = met.data;
  // No cast at all — never extracted or never published — is no section.
  if (!met.isError && (!data || (data.characters.length === 0 && data.unmet_count === 0))) {
    return null;
  }
  const metCount = data?.characters.length ?? 0;
  const unmet = data?.unmet_count ?? 0;

  return (
    <section className="mt-10" data-testid="reader-characters">
      <div className="overflow-hidden rounded-card border border-line bg-surface">
        <button
          type="button"
          data-testid="cast-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((was) => !was)}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-start transition-colors hover:bg-sunk"
        >
          <span className="min-w-0">
            <span className="block text-sm font-medium">{t.reader.characters}</span>
            {data && (
              <span className="block text-xs text-muted" data-testid="cast-summary">
                {t.reader.castSummary(metCount, unmet)}
              </span>
            )}
          </span>
          <Chevron open={expanded} />
        </button>

        {expanded && (
          <div className="border-t border-line">
            {met.isError && (
              <p role="alert" className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
                {t.reader.castFailed}
              </p>
            )}
            {data && metCount === 0 && (
              <p data-testid="cast-empty" className="px-4 py-3 text-sm text-muted">
                {t.reader.castEmpty}
              </p>
            )}
            {metCount > 0 && (
              <CharacterList list={data!.characters} words={(c) => c.introduction} />
            )}
          </div>
        )}

        {/* The warning is the way in: it is on the row before anything is
            shown, so one tap is an informed one and needs no confirmation. */}
        <button
          type="button"
          data-testid="cast-spoilers"
          aria-expanded={arcs}
          onClick={() => setArcs((was) => !was)}
          className="flex w-full items-center justify-between gap-3 border-t border-amber-400/60 bg-amber-50 px-4 py-3 text-start transition-colors hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-900/40"
        >
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
              <WarningIcon />
              {t.reader.arcsTitle}
              <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-950">
                {t.reader.spoilers}
              </span>
            </span>
            {!arcs && (
              <span className="mt-1 block text-xs leading-5 text-amber-800/80 dark:text-amber-300/80">
                {t.reader.arcsWarning}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1.5 rounded-control border border-amber-400 px-2.5 py-1 text-xs font-medium text-amber-800 dark:text-amber-200">
            <EyeIcon shut={arcs} />
            {arcs ? t.reader.hideArcs : t.reader.showArcs}
          </span>
        </button>

        {arcs && (
          <div data-testid="arcs" className="border-t border-amber-400/60">
            {arcCast.isPending && (
              <Pending label={t.reader.arcsLoading} className="space-y-3 px-4 py-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Placeholder className="size-10 shrink-0 rounded-full" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Placeholder className="h-3.5 w-2/5 rounded-full" />
                      <Placeholder className="h-3 w-4/5 rounded-full" />
                    </div>
                  </div>
                ))}
              </Pending>
            )}
            {arcCast.isError && (
              <p role="alert" className="px-4 py-3 text-sm text-red-600 dark:text-red-400">
                {t.reader.castFailed}
              </p>
            )}
            {arcCast.data && (
              <CharacterList
                list={arcCast.data.characters}
                words={(c) => c.description || c.introduction}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** CharacterList is one row per character; a row opens to its words. */
function CharacterList({
  list,
  words,
}: {
  list: ReaderCharacter[];
  words: (c: ReaderCharacter) => string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <ul className="divide-y divide-line">
      {list.map((c) => {
        const text = words(c);
        return (
          <li key={c.id} data-testid="reader-character">
            <button
              type="button"
              aria-expanded={open === c.id}
              disabled={!text}
              onClick={() => setOpen((was) => (was === c.id ? null : c.id))}
              className="flex w-full items-start gap-3 px-4 py-3 text-start transition-colors hover:bg-sunk disabled:hover:bg-transparent"
            >
              {/* The face the book was published with (ADR-0077). The circle
                  is there either way, so a cast with no portraits reads as a
                  list rather than as a page with holes in it. */}
              <CharacterFace character={c} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium" dir="auto">
                  {c.name}
                  {c.role && <span className="font-normal text-muted"> · {c.role}</span>}
                </span>
                {open === c.id && text && (
                  <span className="mt-1 block text-sm leading-6 text-ink-soft" dir="auto">
                    {text}
                  </span>
                )}
              </span>
              {text && <Chevron open={open === c.id} small />}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function EyeIcon({ shut }: { shut: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {shut && <path d="m3 3 18 18" />}
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function Chevron({ open, small = false }: { open: boolean; small?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`${small ? "mt-1 size-4" : "size-5"} shrink-0 text-muted transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
